<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { dataTimeLabel } from '../offline/cache';
import { opLabel, pendingText } from '../offline/ops';
import { useOfflineStore } from '../stores/offline';

/**
 * Lot AW (maquette, repères 31, 33, 35) : bandeau sous l'en-tête. Sans réseau : « Hors connexion · données de
 * 14 h 32 » et le compteur « 3 changements en attente ». Au retour du réseau : l'envoi, puis « Connecté · à jour à
 * 15 h 06 ». Les changements refusés (« Réessayer » / « Abandonner ») et ceux à trancher restent signalés.
 */
const emit = defineEmits<{ conflicts: [] }>();
const offline = useOfflineStore();

const pendingCount = computed(() => offline.pending.length);
const dataLabel = computed(() => (offline.shownDataAt ? dataTimeLabel(offline.shownDataAt) : null));
const showFailed = ref(false);

/** « Connecté · à jour à 15 h 06 » pendant quelques secondes après l'envoi. */
const synced = ref(false);
let syncedTimer: ReturnType<typeof setTimeout> | undefined;
watch(
  () => offline.syncedAt,
  (at) => {
    clearTimeout(syncedTimer);
    synced.value = !!at;
    if (at) syncedTimer = setTimeout(() => (synced.value = false), 6000);
  },
);
onBeforeUnmount(() => clearTimeout(syncedTimer));

const issues = computed(() => offline.conflicts.length > 0 || offline.failed.length > 0);
</script>

<template>
  <div class="offline-zone" data-testid="offline-zone">
    <div v-if="!offline.online" class="offline-bar" role="status" data-testid="offline-banner">
      <i aria-hidden="true"></i>
      <span
        >Hors connexion<template v-if="dataLabel"> · données de {{ dataLabel }}</template></span
      >
      <span v-if="pendingCount > 0" class="cnt" data-testid="offline-pending">{{
        pendingText(pendingCount)
      }}</span>
    </div>
    <div v-else-if="offline.replaying" class="online-bar" role="status" data-testid="offline-sending">
      <i aria-hidden="true"></i>Envoi des changements gardés…
    </div>
    <div v-else-if="synced && offline.syncedAt" class="online-bar" role="status" data-testid="offline-synced">
      <i aria-hidden="true"></i>Connecté · à jour à {{ dataTimeLabel(offline.syncedAt) }}
    </div>
    <div v-else-if="pendingCount > 0" class="online-bar wait" role="status" data-testid="offline-waiting">
      <i aria-hidden="true"></i>{{ pendingText(pendingCount) }}
      <template v-if="offline.sessionExpired"> · reconnecte-toi pour les envoyer</template>
    </div>

    <div v-if="issues" class="offline-issues" data-testid="offline-issues">
      <span v-if="offline.conflicts.length > 0">
        {{ offline.conflicts.length }} changement{{ offline.conflicts.length > 1 ? 's' : '' }} à trancher
        <button class="linkbtn" type="button" data-testid="offline-show-conflicts" @click="emit('conflicts')">
          Voir
        </button>
      </span>
      <span v-if="offline.failed.length > 0">
        {{ offline.failed.length }} changement{{ offline.failed.length > 1 ? 's' : '' }} non envoyé{{
          offline.failed.length > 1 ? 's' : ''
        }}
        <button
          class="linkbtn"
          type="button"
          :aria-expanded="showFailed ? 'true' : 'false'"
          data-testid="offline-show-failed"
          @click="showFailed = !showFailed"
        >
          {{ showFailed ? 'Masquer' : 'Voir' }}
        </button>
      </span>
      <ul v-if="showFailed && offline.failed.length > 0" class="offline-failed" data-testid="offline-failed">
        <li v-for="op in offline.failed" :key="op.id">
          <span
            ><b>{{ opLabel(op) }}</b
            ><small v-if="op.error"> · {{ op.error.message }}</small></span
          >
          <span class="row-actions">
            <button class="btn sm" type="button" :disabled="!offline.online" @click="offline.retry(op.id)">
              Réessayer
            </button>
            <button class="btn sm ghost" type="button" @click="offline.abandon(op.id)">Abandonner</button>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
